import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { map, exhaustMap, catchError, finalize } from 'rxjs/operators';
import { UsersService } from '../../_services/users.service';
import { UsersActions } from '../_actions/users.actions';
import { LayoutActions } from '../_actions/layout.actions';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { AppState } from '../index';
import {
  LayoutUtilsServices,
  MessageType,
} from '../../_services/layout-utils.services';
import { currentUsersFilters } from '../_selectors/users.selectors';
import { RolesService } from '../../_services/roles.service';

@Injectable()
export class UsersEffects {
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._usersService.findAll(action.filters).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) =>
            UsersActions.loadUsersSuccess({ items: res.data, total: res.total })
          ),
          catchError(() => EMPTY)
        );
      })
    )
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUser),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._usersService.findOne(action.id).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((data) => UsersActions.loadUserSuccess({ data })),
          catchError(() => EMPTY)
        );
      })
    )
  );

  addUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.addUser),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._usersService.create(action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha creado el registro exitosamente',
              type: MessageType.Susses,
            });
            this.router.navigate(['/users']);
            return UsersActions.addUserSuccess({ data: res });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al crear el registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.editUser),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._usersService.update(action.id, action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha actualizado el registro exitosamente',
              type: MessageType.Susses,
            });
            this.router.navigate(['/users']);
            return UsersActions.editUserSuccess({ id: action.id, data: res });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al actualizar el registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  removeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.removeUser),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._usersService.remove(action.id).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha eliminado el registro exitosamente',
              type: MessageType.Susses,
            });
            this.store.dispatch(
              UsersActions.loadUsers({ filters: this.filters })
            );
            return UsersActions.removeUserSuccess({ id: action.id });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al eliminar el registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUserRoles),
      exhaustMap((action) => {
        return this._rolesService.findAll({ offset: 0, limit: 1000 }).pipe(
          map((res) => UsersActions.loadUserRolesSuccess({ items: res.data })),
          catchError(() => EMPTY)
        );
      })
    )
  );

  loadPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadPoints),
      exhaustMap(() => {
        return this._usersService.getPoints().pipe(
          map((res) =>
            UsersActions.loadPointsSuccess({
              points_earned: res.points_earned,
              points_used: res.points_used,
              points_available: res.points_available,
            })
          ),
          catchError((error) => {
            return [UsersActions.loadPointsFailure({ error })];
          })
        );
      })
    )
  );

  filters: any = {};
  constructor(
    private actions$: Actions,
    private _usersService: UsersService,
    private _rolesService: RolesService,
    private _layoutUtilsServices: LayoutUtilsServices,
    private store: Store<AppState>,
    private router: Router
  ) {
    this.store
      .pipe(select(currentUsersFilters))
      .subscribe((data) => (this.filters = data));
  }
}
